import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Link } from 'react-router-dom';
import {
  duration,
  easing,
  gradient,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  status,
  typography,
} from '../../../../../style-utils/theme';
import { touch, up } from '../../../../../style-utils/media-queries';
import { SCALE } from '../../../../../motion';
import { ToggleChip } from '../../../../toggle-chip/toggle-chip';

/**
 * THE GUEST GUIDE — styles (plan S7.7). Tokens only, phone first (iPhone
 * WebKit, 320 px): nothing scrolls sideways, every control is 44 px under a
 * thumb, user text wraps anywhere, the end of the page clears
 * var(--safe-bottom). S3/S6's guest kit (public-stay-page.styled.tsx,
 * guest-checkin.styled.tsx) carries the buttons and the header lines.
 *
 * NO COMPONENT SELECTORS (the CRA dev build crashes on them) and NO BACKTICKS
 * inside a comment in a template below.
 */

const press = css`
  transition: color ${duration.fast} ${easing.standard}, background-color ${duration.fast} ${easing.standard},
    border-color ${duration.fast} ${easing.standard}, box-shadow ${duration.base} ${easing.standard},
    transform ${duration.instant} ${easing.spring};

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(${SCALE.pressed});
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/* ── the frame ── */

export const Frame = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
`;

export const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
`;

export const FrameEnd = styled.footer`
  display: flex;
  justify-content: center;
  padding-bottom: calc(${spacing.lg} + var(--safe-bottom));
`;

export const NavList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  margin: 0;
  padding: 0;
  list-style: none;
  min-width: 0;
`;

const chip = css`
  -webkit-appearance: none;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  min-height: ${layout.compactControlHeight};
  padding: ${spacing.half} ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.pill};
  background: ${palette.surface};
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  text-align: center;
  text-decoration: none;
  overflow-wrap: anywhere;
  cursor: pointer;
  ${press};

  &:hover {
    border-color: ${palette.lagoon};
    background: ${palette.irisTintSoft};
  }

  &[aria-current='page'] {
    border-color: ${palette.iris};
    background: ${palette.irisTint};
    color: ${palette.irisDeep};
  }

  ${touch} {
    min-height: ${layout.touchTarget};
  }
`;

export const NavChipLink = styled(Link)`
  ${chip};
`;

export const NavChipButton = styled.button`
  ${chip};
`;

const quietLink = css`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.half};
  border: 0;
  border-radius: ${radius.xs};
  background: transparent;
  color: ${palette.lagoon};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const QuietLink = styled(Link)`
  ${quietLink};
`;

export const QuietButton = styled.button`
  ${quietLink};
`;

export const QuietAnchor = styled.a`
  ${quietLink};
`;

/* ── page titles ── */

export const Title = styled.h1`
  margin: 0;
  font-family: ${typography.display};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.heavy};
  line-height: ${typography.leading.snug};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
  overflow-wrap: anywhere;

  &:focus {
    outline: none;
  }
`;

export const SectionHeading = styled.h2`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const SmallHeading = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const Eyebrow = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.slate};
`;

/* ── text ── */

/** Host-written text: its line breaks kept, wraps anywhere, lang set by the caller. */
export const HostText = styled.p`
  margin: 0;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  line-height: ${typography.leading.relaxed};
  color: ${palette.inkSoft};
  white-space: pre-line;
  overflow-wrap: anywhere;
`;

export const Muted = styled.p`
  margin: 0;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

/* Inherits the size and colour of the sentence it sits in. */
export const TimeText = styled.time`
  font-weight: ${typography.weight.semibold};
  overflow-wrap: anywhere;
`;

/** A door code or a Wi-Fi value: large, monospace, never cut. */
export const CodeValue = styled.p`
  margin: 0;
  min-width: 0;
  font-family: ${typography.mono};
  font-size: ${typography.size.xxl}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  letter-spacing: ${typography.tracking.wide};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const LargeValue = styled.p`
  margin: 0;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const ValueLabel = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.slate};
`;

/* ── cards ── */

export const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.sm};

  ${up('sm')} {
    padding: ${spacing.md};
  }
`;

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
`;

/* ── link buttons (tel:, maps) in the AppButton look ── */

const linkButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  min-height: ${layout.buttonHeight};
  padding: ${spacing.xs} ${spacing.md};
  border-radius: ${radius.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  text-align: center;
  text-decoration: none;
  overflow-wrap: anywhere;
  cursor: pointer;
  ${press};
`;

export const SecondaryAnchor = styled.a`
  ${linkButton};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  color: ${palette.ink};

  &:hover {
    border-color: ${palette.teal};
    background: ${palette.irisTintSoft};
  }
`;

export const PrimaryAnchor = styled.a`
  ${linkButton};
  border: 1px solid transparent;
  background: ${gradient.primary};
  color: ${palette.white};
  box-shadow: ${shadow.brand};

  &:hover {
    background: ${gradient.primaryHover};
    box-shadow: ${shadow.brandHover};
  }
`;

export const PrimaryRouterLink = styled(Link)`
  ${linkButton};
  border: 1px solid transparent;
  background: ${gradient.primary};
  color: ${palette.white};
  box-shadow: ${shadow.brand};

  &:hover {
    background: ${gradient.primaryHover};
    box-shadow: ${shadow.brandHover};
  }
`;

/* ── the access notice ── */

export const Notice = styled.div<{ $tone: 'brand' | 'neutral' }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm};
  border: 1px solid ${({ $tone }) => ($tone === 'brand' ? palette.irisTint : palette.line)};
  border-radius: ${radius.sm};
  background: ${({ $tone }) => status[$tone].bg};
  color: ${({ $tone }) => status[$tone].fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  overflow-wrap: anywhere;
`;

export const NoticeText = styled.p`
  margin: 0;
`;

/* ── the hub ── */

export const Hero = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
  min-height: ${spacing.huge};
  overflow: hidden;
  border-radius: ${radius.lg};
  background: ${palette.surfaceMuted};
`;

export const HeroWithPhoto = styled(Hero)`
  min-height: calc(${spacing.huge} * 3);
`;

export const HeroImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const HeroPanel = styled.div`
  position: relative;
  width: 100%;
  padding: ${spacing.sm};
  background: ${palette.surfaceGlass};
  -webkit-backdrop-filter: blur(20px);
  backdrop-filter: blur(20px);
`;

export const HeroTitle = styled(Title)`
  font-size: ${typography.size.xxl}px;
  line-height: ${typography.leading.tight};
  letter-spacing: ${typography.tracking.tight};

  ${up('sm')} {
    font-size: ${typography.size.display}px;
  }
`;

export const HostRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.sm};
  min-width: 0;
`;

export const Avatar = styled.img`
  flex: none;
  width: ${spacing.xxxl};
  height: ${spacing.xxxl};
  border-radius: ${radius.circle};
  object-fit: cover;
  background: ${palette.surfaceMuted};
`;

export const StayLines = styled.dl`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.xs};
  margin: 0;

  ${up('sm')} {
    grid-template-columns: 1fr 1fr;
  }

  dt {
    font-family: ${typography.ui};
    font-size: ${typography.size.xs}px;
    font-weight: ${typography.weight.medium};
    color: ${palette.slate};
  }

  dd {
    margin: 0;
  }
`;

export const CardsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
`;

export const CardGrid = styled.ul`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.sm};
  margin: 0;
  padding: 0;
  list-style: none;

  ${up('sm')} {
    grid-template-columns: 1fr 1fr;
  }
`;

const sectionCard = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.half};
  width: 100%;
  height: 100%;
  min-height: ${layout.touchTarget};
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.sm};
  color: ${palette.ink};
  font-family: ${typography.ui};
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  ${press};

  &:hover {
    transform: translateY(-2px);
    border-color: ${palette.lineStrong};
    box-shadow: ${shadow.md};
  }
`;

export const SectionCardLink = styled(Link)`
  ${sectionCard};
`;

export const SectionCardButton = styled.button`
  ${sectionCard};
`;

export const CardTitle = styled.span`
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.bold};
  line-height: ${typography.leading.snug};
  overflow-wrap: anywhere;
`;

export const CardSub = styled.span`
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

/* ── arrival ── */

export const StepList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const StepItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.sm};

  ${up('sm')} {
    padding: ${spacing.md};
  }
`;

export const StepNumber = styled.span`
  font-family: ${typography.mono};
  font-weight: ${typography.weight.semibold};
  color: ${palette.lagoon};
`;

export const PhotoButton = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  border-radius: ${radius.sm};
  background: ${palette.surfaceMuted};
  overflow: hidden;
  cursor: zoom-in;

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
  }
`;

/* ── essentials ── */

export const TileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.sm};

  ${up('sm')} {
    grid-template-columns: 1fr 1fr;
  }
`;

export const Accordion = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};

  details {
    border: 1px solid ${palette.line};
    border-radius: ${radius.sm};
    background: ${palette.surface};
  }

  summary {
    display: flex;
    align-items: center;
    min-height: ${layout.touchTarget};
    padding: ${spacing.xs} ${spacing.sm};
    border-radius: ${radius.sm};
    font-family: ${typography.ui};
    font-size: ${typography.size.base}px;
    font-weight: ${typography.weight.semibold};
    color: ${palette.ink};
    cursor: pointer;
    list-style: none;
  }

  summary::-webkit-details-marker {
    display: none;
  }

  summary::marker {
    content: '';
  }

  summary:hover {
    background: ${palette.irisTintSoft};
  }

  summary:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  .guide-details-body {
    padding: 0 ${spacing.sm} ${spacing.sm};
  }
`;

export const FaqList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const FaqItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
  padding-bottom: ${spacing.sm};
  border-bottom: 1px solid ${palette.line};
`;

/* ── local tips ── */

export const TipList = styled.ul`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.sm};
  margin: 0;
  padding: 0;
  list-style: none;

  ${up('sm')} {
    grid-template-columns: 1fr 1fr;
  }
`;

export const TipCard = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.sm};
`;

export const TipPhoto = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: ${radius.sm};
  background: ${palette.surfaceMuted};
`;

/* ── check-out ── */

export const Checklist = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const CheckItem = styled(ToggleChip)`
  width: 100%;
  justify-content: flex-start;
  min-height: ${layout.touchTarget};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.medium};
  text-align: left;
  white-space: normal;
`;

export const CheckMark = styled.span<{ $on: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${spacing.md};
  height: ${spacing.md};
  margin-right: ${spacing.xs};
  border: 1px solid ${({ $on }) => ($on ? palette.iris : palette.lineStrong)};
  border-radius: ${radius.xs};
  background: ${({ $on }) => ($on ? palette.iris : palette.surface)};
  color: ${palette.white};
`;

/* ── photos ── */

export const Mosaic = styled.ul`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.xs};
  margin: 0;
  padding: 0;
  list-style: none;

  li:first-of-type {
    grid-column: 1 / -1;
  }
`;

export const MosaicTile = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  border-radius: ${radius.sm};
  background: ${palette.surfaceMuted};
  overflow: hidden;
  cursor: zoom-in;
  ${press};

  img {
    display: block;
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
  }
`;

export const MosaicLead = styled(MosaicTile)`
  img {
    aspect-ratio: 16 / 9;
  }
`;

export const ShareSlot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.half};
`;

export const ShareReason = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

/* ── the help strip ── */

export const Help = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
  padding: ${spacing.sm};
  border-radius: ${radius.md};
  background: ${palette.surfaceMuted};

  ${up('sm')} {
    padding: ${spacing.md};
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  min-width: 0;
`;

/* ── loading ── */

export const SkeletonCards = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.sm};
  margin-top: ${spacing.md};

  ${up('sm')} {
    grid-template-columns: 1fr 1fr;
  }
`;
